/**
 * Serve-only 2D positioning: hip heat in table UV + time-to-ready after contact.
 *
 * Heat = server hip midpoint at serve contact, projected onto the table plane.
 * Time-to-ready = ms from contact until hips settle back near the pre-toss
 * still stance (low speed, stance restored, depth/lateral close to baseline).
 *
 * Keep in sync with Backend/src/utils/servePositionMetrics.js
 */

import {
  TABLE_LENGTH_M,
  TABLE_WIDTH_M,
  depthBehindEndU,
  imageToTableUV,
  tableHomography,
  tableUvDeltaM,
} from "./tableUv";

function servePlayerOf(event) {
  if (event?.player === "B" || event?.server_side === "right") return "B";
  return "A";
}

const L_HIP = 11;
const R_HIP = 12;
const L_KNEE = 13;
const R_KNEE = 14;
const L_ANKLE = 15;
const R_ANKLE = 16;
const MIN_JOINT_SCORE = 0.2;

const READY_MIN_MS = 80;
const READY_MAX_MS = 1500;
/** Settle speed on the table-plane proxy (m/s). */
const READY_SPEED_MPS = 0.55;
const READY_DEPTH_M = 0.3;
const READY_LATERAL_M = 0.34;
const STANCE_MIN_FRAC = 0.68;
const STANCE_MAX_FRAC = 1.6;
const KNEE_UPRIGHT_SLACK_DEG = 14;
const READY_UNDER_MS = 800;

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function jointOk(j, minScore = MIN_JOINT_SCORE) {
  return (
    j &&
    Number.isFinite(Number(j.x)) &&
    Number.isFinite(Number(j.y)) &&
    (j.score == null || Number(j.score) >= minScore)
  );
}

function playerSide(player) {
  return player === "B" ? "right" : "left";
}

function jointsOf(sample, player) {
  const side = sample?.[playerSide(player)];
  const joints = side?.joints;
  return Array.isArray(joints) ? joints : null;
}

function midpoint(a, b) {
  if (!jointOk(a) || !jointOk(b)) return null;
  return {
    x: (Number(a.x) + Number(b.x)) / 2,
    y: (Number(a.y) + Number(b.y)) / 2,
  };
}

function dist(a, b) {
  if (!jointOk(a) || !jointOk(b)) return null;
  return Math.hypot(Number(a.x) - Number(b.x), Number(a.y) - Number(b.y));
}

function hipPoint(joints) {
  if (!joints) return null;
  return midpoint(joints[L_HIP], joints[R_HIP]);
}

function stanceWidth(joints) {
  if (!joints) return null;
  const ankles = dist(joints[L_ANKLE], joints[R_ANKLE]);
  if (ankles != null && ankles > 1e-4) return ankles;
  const hips = dist(joints[L_HIP], joints[R_HIP]);
  return hips != null && hips > 1e-4 ? hips : null;
}

function meanKneeDeg(sample, player, joints) {
  const angles = sample?.[playerSide(player)]?.angles || {};
  const left = num(angles.left_knee);
  const right = num(angles.right_knee);
  if (left != null && right != null) return (left + right) / 2;
  if (left != null) return left;
  if (right != null) return right;
  if (!joints) return null;
  const interior = (hip, knee, ankle) => {
    if (!jointOk(hip) || !jointOk(knee) || !jointOk(ankle)) return null;
    const v1x = Number(hip.x) - Number(knee.x);
    const v1y = Number(hip.y) - Number(knee.y);
    const v2x = Number(ankle.x) - Number(knee.x);
    const v2y = Number(ankle.y) - Number(knee.y);
    const n1 = Math.hypot(v1x, v1y);
    const n2 = Math.hypot(v2x, v2y);
    if (n1 < 1e-6 || n2 < 1e-6) return null;
    const cos = Math.max(-1, Math.min(1, (v1x * v2x + v1y * v2y) / (n1 * n2)));
    return (Math.acos(cos) * 180) / Math.PI;
  };
  const a = interior(joints[L_HIP], joints[L_KNEE], joints[L_ANKLE]);
  const b = interior(joints[R_HIP], joints[R_KNEE], joints[R_ANKLE]);
  if (a != null && b != null) return (a + b) / 2;
  return a ?? b;
}

function nearestSample(samples, tMs) {
  const target = num(tMs);
  if (target == null || !Array.isArray(samples) || !samples.length) return null;
  let best = samples[0];
  let bestD = Math.abs((num(best.t_ms) ?? 0) - target);
  for (let i = 1; i < samples.length; i += 1) {
    const d = Math.abs((num(samples[i].t_ms) ?? 0) - target);
    if (d < bestD) {
      best = samples[i];
      bestD = d;
    }
  }
  return best;
}

function samplesInRange(samples, t0, t1) {
  const a = num(t0);
  const b = num(t1);
  if (a == null || b == null || b < a) return [];
  return samples.filter((s) => {
    const t = num(s.t_ms);
    return t != null && t >= a && t <= b;
  });
}

function poseSnapshot(sample, player, homography) {
  if (!sample) return null;
  const joints = jointsOf(sample, player);
  const hip = hipPoint(joints);
  if (!hip) return null;
  const uv = homography
    ? imageToTableUV(hip.x, hip.y, null, homography)
    : null;
  return {
    t_ms: num(sample.t_ms),
    x: hip.x,
    y: hip.y,
    u: uv?.u ?? null,
    v: uv?.v ?? null,
    stance: stanceWidth(joints),
    knee_deg: meanKneeDeg(sample, player, joints),
  };
}

export function serveContactMs(event) {
  const contact = num(event?.phases?.contact?.t_start_ms) ?? num(event?.t_contact_ms);
  if (contact != null) return contact;
  const peak = num(event?.t_peak_ms) ?? num(event?.t_ms);
  return peak != null ? peak + 550 : null;
}

export function serveBaselineMs(event) {
  return (
    num(event?.t_steady_ms) ??
    num(event?.phases?.preparation?.t_start_ms) ??
    num(event?.t_prep_start_ms) ??
    null
  );
}

function lateralZone(v) {
  const vv = num(v);
  if (vv == null) return null;
  if (vv < 0.33) return "wide_top";
  if (vv > 0.67) return "wide_bottom";
  return "center";
}

function depthZone(depthU) {
  const d = num(depthU);
  if (d == null) return null;
  if (d < 0.06) return "close";
  if (d < 0.2) return "mid";
  return "far";
}

function isReady(snap, baseline, speedMps) {
  if (!snap || !baseline) return false;
  if (speedMps != null && speedMps > READY_SPEED_MPS) return false;
  const stance0 = num(baseline.stance);
  const stance1 = num(snap.stance);
  if (stance0 != null && stance0 > 1e-4 && stance1 != null) {
    const frac = stance1 / stance0;
    if (frac < STANCE_MIN_FRAC || frac > STANCE_MAX_FRAC) return false;
  }
  const knee0 = num(baseline.knee_deg);
  const knee1 = num(snap.knee_deg);
  if (knee0 != null && knee1 != null && knee1 > knee0 + KNEE_UPRIGHT_SLACK_DEG) {
    return false;
  }
  if (baseline.u != null && snap.u != null && baseline.v != null && snap.v != null) {
    const depthM = Math.abs(snap.u - baseline.u) * TABLE_LENGTH_M;
    const latM = Math.abs(snap.v - baseline.v) * TABLE_WIDTH_M;
    if (depthM > READY_DEPTH_M || latM > READY_LATERAL_M) return false;
    return true;
  }
  const img = Math.hypot(snap.x - baseline.x, snap.y - baseline.y);
  return img <= 0.06;
}

function speedBetween(a, b) {
  if (!a || !b) return null;
  const dt = ((num(b.t_ms) ?? 0) - (num(a.t_ms) ?? 0)) / 1000;
  if (!(dt > 1e-4)) return 0;
  if (a.u != null && b.u != null && a.v != null && b.v != null) {
    const m = tableUvDeltaM(b.u - a.u, b.v - a.v);
    return m != null ? m / dt : null;
  }
  return Math.hypot(b.x - a.x, b.y - a.y) / dt;
}

function median(values) {
  const xs = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (!xs.length) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

function summarizeReady(times) {
  const finite = times.filter((v) => Number.isFinite(v));
  const timeouts = times.length - finite.length;
  const under = finite.filter((v) => v <= READY_UNDER_MS).length;
  return {
    count: finite.length,
    timeout_count: timeouts,
    median_ms: finite.length ? Math.round(median(finite)) : null,
    avg_ms: finite.length
      ? Math.round(finite.reduce((s, v) => s + v, 0) / finite.length)
      : null,
    min_ms: finite.length ? Math.round(Math.min(...finite)) : null,
    max_ms: finite.length ? Math.round(Math.max(...finite)) : null,
    pct_ready_under_800: times.length
      ? Math.round((under / times.length) * 100)
      : null,
  };
}

function metricsForEvent(event, samples, homography, nextPrepMs) {
  const player = servePlayerOf(event);
  const tContact = serveContactMs(event);
  const tBase = serveBaselineMs(event);
  const baseline = poseSnapshot(nearestSample(samples, tBase ?? tContact), player, homography);
  const contact = poseSnapshot(nearestSample(samples, tContact), player, homography);
  const depthU = contact ? depthBehindEndU(contact.u, player) : null;
  const tReadyCap = Math.min(
    (tContact ?? 0) + READY_MAX_MS,
    nextPrepMs != null ? nextPrepMs - 40 : (tContact ?? 0) + READY_MAX_MS
  );
  const window = samplesInRange(
    samples,
    (tContact ?? 0) + READY_MIN_MS,
    tReadyCap
  );
  let tReady = null;
  let prev = contact;
  for (const sample of window) {
    const snap = poseSnapshot(sample, player, homography);
    if (!snap) continue;
    const spd = speedBetween(prev || snap, snap);
    if (isReady(snap, baseline || contact, spd)) {
      tReady = snap.t_ms;
      break;
    }
    prev = snap;
  }
  const timeToReady =
    tReady != null && tContact != null ? Math.max(0, Math.round(tReady - tContact)) : null;
  return {
    hip_u: contact?.u ?? null,
    hip_v: contact?.v ?? null,
    hip_x_norm: contact?.x ?? null,
    hip_y_norm: contact?.y ?? null,
    depth_behind_u: depthU,
    lateral_v: contact?.v ?? null,
    lateral_zone: lateralZone(contact?.v),
    depth_zone: depthZone(depthU),
    t_contact_ms: tContact,
    t_ready_ms: tReady,
    time_to_ready_ms: timeToReady,
    ready_timeout: timeToReady == null,
    stance_width_norm: contact?.stance ?? null,
  };
}

export function attachServePositionMetrics(detection, samples, tableCorners) {
  if (!detection || typeof detection !== "object") return detection;
  const eventsIn = Array.isArray(detection.events) ? detection.events : [];
  const ordered = Array.isArray(samples)
    ? [...samples].sort((a, b) => (num(a.t_ms) || 0) - (num(b.t_ms) || 0))
    : [];
  const homography = tableHomography(tableCorners);
  const events = eventsIn.map((event, i) => {
    const nextPrep =
      num(eventsIn[i + 1]?.t_prep_start_ms) ??
      num(eventsIn[i + 1]?.phases?.preparation?.t_start_ms) ??
      num(eventsIn[i + 1]?.t_ms);
    return {
      ...event,
      ...metricsForEvent(event, ordered, homography, nextPrep),
    };
  });
  const points = events
    .filter((e) => e.hip_u != null && e.hip_v != null)
    .map((e) => ({
      player: servePlayerOf(e),
      index: e.index ?? null,
      u: e.hip_u,
      v: e.hip_v,
      depth_behind_u: e.depth_behind_u,
      time_to_ready_ms: e.time_to_ready_ms,
      ready_timeout: Boolean(e.ready_timeout),
      t_ms: e.t_peak_ms ?? e.t_ms,
    }));
  const timesAll = events.map((e) => e.time_to_ready_ms);
  const timesA = events.filter((e) => servePlayerOf(e) === "A").map((e) => e.time_to_ready_ms);
  const timesB = events.filter((e) => servePlayerOf(e) === "B").map((e) => e.time_to_ready_ms);
  return {
    ...detection,
    events,
    heat: { points },
    time_to_ready: {
      ...summarizeReady(timesAll),
      player_a: summarizeReady(timesA),
      player_b: summarizeReady(timesB),
    },
  };
}

export const SERVE_READY_CONSTANTS = {
  READY_MIN_MS,
  READY_MAX_MS,
  READY_SPEED_MPS,
  READY_UNDER_MS,
  TABLE_LENGTH_M,
  TABLE_WIDTH_M,
};
